// src/pages/offlineSales/CustomerListPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Edit, History, RefreshCw, Upload, Download } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getCustomers, getCustomerTypes } from '../../services/api/offlineSales/customers';
import TruncatedText from '../../components/ui/TruncatedText/TruncatedText';
import CustomerModal from './components/CustomerModal';
import PurchaseHistoryModal from './components/PurchaseHistoryModal';
import { toTemplateRow, downloadCustomerWorkbook } from './customerImportColumns';
import styles from './CustomerListPage.module.css';

const EXPORT_BATCH_SIZE = 500;

// ─── Main Page ────────────────────────────────────────────────────────────────
const CustomerListPage = () => {
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { error } = useAlert();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 20, total: 0 });
  const [sorting, setSorting] = useState({ orderBy: 'createdAt', orderDirection: 'DESC' });
  const [filters, setFilters] = useState({ search: '', customerTypeIds: [] });
  const [customerTypes, setCustomerTypes] = useState([]);

  const [showFilters, setShowFilters] = useState(true);
  const [modalCustomer, setModalCustomer] = useState(null); // null = closed, undefined = create, obj = edit
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [exporting, setExporting] = useState(false);

  const buildFilterParams = useCallback(() => {
    const params = {
      orderBy: sorting.orderBy,
      orderDirection: sorting.orderDirection,
      ...(filters.search ? { search: filters.search } : {}),
    };
    if (filters.customerTypeIds?.length > 0) params.customerTypeIds = filters.customerTypeIds.join(',');
    return params;
  }, [sorting, filters]);

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...buildFilterParams(), page: pagination.currentPage, limit: pagination.pageSize };
      const result = await getCustomers(params);
      setCustomers(result.customers || []);
      setPagination(p => ({ ...p, total: result.total || 0 }));
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, buildFilterParams, error]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = buildFilterParams();
      const first = await getCustomers({ ...params, page: 1, limit: 1 });
      const totalPages = Math.ceil((first.total || 0) / EXPORT_BATCH_SIZE);
      const all = [];
      for (let page = 1; page <= totalPages; page++) {
        const result = await getCustomers({ ...params, page, limit: EXPORT_BATCH_SIZE });
        all.push(...(result.customers || []));
      }
      await downloadCustomerWorkbook(all.map(toTemplateRow), `customers-${new Date().toISOString().split('T')[0]}`);
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to export customers');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  useEffect(() => {
    getCustomerTypes().then(data => setCustomerTypes(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const filterConfig = [
    {
      key: 'search',
      type: 'text',
      label: t('offlineSales.searchCustomers'),
      placeholder: t('offlineSales.searchCustomers'),
    },
    {
      key: 'customerTypeIds',
      type: 'multiselect',
      label: t('offlineSales.customerType'),
      placeholder: t('common.all', 'All'),
      options: customerTypes,
      displayKey: 'label',
      valueKey: 'id',
      searchKey: 'label',
    },
  ];

  const columns = [
    {
      key: 'cusCode',
      header: t('offlineSales.cusCode'),
      render: (row) => row.cusCode || '—',
    },
    {
      key: 'name',
      header: t('offlineSales.customerName'),
      sortable: true,
      render: (row) => (
        <TruncatedText text={row.name} className={styles.customerName} />
      ),
    },
    {
      key: 'phone',
      header: t('offlineSales.customerPhone'),
      render: (row) => row.phone || '—',
    },
    {
      key: 'email',
      header: t('offlineSales.customerEmail'),
      render: (row) => row.email || '—',
    },
    {
      key: 'city',
      header: t('offlineSales.customerCity'),
      render: (row) => row.city?.name || '—',
    },
    {
      key: 'loyaltyPoints',
      header: t('offlineSales.loyaltyPointsLabel'),
      align: 'right',
      sortable: true,
      render: (row) => <span className={styles.points}>{row.loyaltyPoints || 0} pts</span>,
    },
    {
      key: 'totalSpend',
      header: t('offlineSales.totalSpend'),
      align: 'right',
      sortable: true,
      render: (row) => formatCurrency(row.totalSpend || 0),
    },
    {
      key: 'customerType',
      header: t('offlineSales.customerType'),
      render: (row) => row.customerType?.label || '—',
    },
    {
      key: 'tinNumber',
      header: t('offlineSales.tinNumber'),
      render: (row) => row.tinNumber || '—',
    },
    {
      key: 'vatNumber',
      header: t('offlineSales.vatNumber'),
      render: (row) => row.vatNumber || '—',
    },
    {
      key: 'isActive',
      header: t('common.status', 'Status'),
      width: '80px',
      render: (row) => (
        <span className={`${styles.badge} ${row.isActive ? styles.active : styles.inactive}`}>
          {row.isActive ? t('offlineSales.activeCustomer') : t('offlineSales.inactiveCustomer')}
        </span>
      ),
    },
  ];

  const renderActions = (row) => (
    <div className={styles.actions}>
      <button
        className={styles.actionBtn}
        onClick={() => setModalCustomer(row)}
        title={t('offlineSales.editCustomer')}
      >
        <Edit size={14} />
      </button>
      <button
        className={styles.actionBtn}
        onClick={() => setHistoryCustomer(row)}
        title={t('offlineSales.viewHistory')}
      >
        <History size={14} />
      </button>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.allPosCustomers')}
        icon={Users}
        actions={
          <div className={styles.headerActions}>
            <button className={styles.refreshBtn} onClick={loadCustomers}>
              <RefreshCw size={16} />
            </button>
            <button className={styles.secondaryBtn} onClick={handleExport} disabled={exporting}>
              <Download size={16} /> {exporting ? t('offlineSales.exporting') : t('offlineSales.exportCustomers')}
            </button>
            <button className={styles.secondaryBtn} onClick={() => navigate('/offline-sales/customers/import')}>
              <Upload size={16} /> {t('offlineSales.importCustomers')}
            </button>
            <button className={styles.primaryBtn} onClick={() => setModalCustomer(undefined)}>
              <Plus size={16} /> {t('offlineSales.addCustomer')}
            </button>
          </div>
        }
      />

      <FilterPanel
        filters={filterConfig}
        values={filters}
        onChange={setFilters}
        show={showFilters}
        onToggle={() => setShowFilters(v => !v)}
      />

      <DataTable
        data={customers}
        columns={columns}
        actions={{ render: renderActions, width: '80px' }}
        loading={loading}
        emptyMessage={t('offlineSales.allPosCustomers')}
        emptyDescription={t('offlineSales.noCustomersFound')}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={{ ...pagination, pageSizeOptions: [10, 20, 50] }}
        onPaginationChange={(changes) => setPagination(p => ({ ...p, ...changes }))}
      />

      {modalCustomer !== null && (
        <CustomerModal
          customer={modalCustomer}
          onClose={() => setModalCustomer(null)}
          onSaved={(saved) => {
            setModalCustomer(null);
            loadCustomers();
          }}
        />
      )}

      {historyCustomer && (
        <PurchaseHistoryModal
          customer={historyCustomer}
          onClose={() => setHistoryCustomer(null)}
        />
      )}
    </div>
  );
};

export default CustomerListPage;
